import { mainApi } from '../../../../../api/mainApi';
import { AccessState } from '../../../../../types';
import { middlewareApi } from '../../../../middleware/middlewareApi';
import {
  CreateMeetingsResponse,
  MeetingsResponse,
  Meeting,
} from '../interfaces/meetings.interface';
import { MeetingPayload, MeetingUpdatePayload } from '../interfaces/meetingsPayload.interface';

export const teacherMeetingsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['Meetings', 'teacherMeetings'],
});
// Define a service using a base URL and expected endpoints
export const teacherMeetingsApi = teacherMeetingsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET meetings
     */
    getCourseMeetings: builder.query<
      MeetingsResponse,
      {
        profileId?: number;
        page: number;
        perPage: number;
        courseId: number;
        search?: string;
        fdesde?: string;
        fhasta?: string;
        activity_id?: number;
      }
    >({
      queryFn: async (
        { page, perPage, profileId, courseId, search, fdesde, fhasta, activity_id },
        { getState }
      ) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<MeetingsResponse>(
            `/courses/${courseId}/activities/mettings`,
            {
              params: {
                user_profile_id,
                page,
                size: perPage,
                sort: 'id',
                type_sort: 'desc',
                course_id: courseId,
                search,
                fdesde,
                fhasta,
                activity_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                (value) => ({ type: 'Meetings', id: value.activity_zoom.activity_id } as const)
              ),
              { type: 'Meetings', id: 'LIST' },
            ]
          : [{ type: 'Meetings', id: 'LIST' }],
    }),

    /**
     * @GET get meet
     */
    getMeetingById: builder.query<
      Meeting,
      {
        profileId?: number;
        courseId: number;
        activity_id?: number;
      }
    >({
      queryFn: async ({ profileId, courseId, activity_id }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.get<Meeting>(`/courses/${courseId}/activities/mettings`, {
            params: {
              user_profile_id,
              course_id: courseId,
              activity_id,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { activity_id }) => [{ type: 'Meetings', id: activity_id }],
    }),

    /**
     * @POST create meeting
     */
    createMeeting: builder.mutation<
      CreateMeetingsResponse,
      { course: number; profileId?: number; payload: MeetingPayload }
    >({
      queryFn: async ({ course, profileId, payload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.post<CreateMeetingsResponse>(
            `/courses/${course}/activities/mettings`,
            {
              user_profile_id,
              ...payload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Meetings', id: 'LIST' }],
    }),

    /**
     * @PUT create meeting
     */
    updateMeeting: builder.mutation<
      CreateMeetingsResponse,
      { course: number; activity: number; profileId?: number; payload: MeetingUpdatePayload }
    >({
      queryFn: async ({ course, activity, profileId, payload }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.put<CreateMeetingsResponse>(
            `/courses/${course}/activities/${activity}/mettings`,
            {
              user_profile_id,
              ...payload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (result, error, { payload, activity }) => [
        { type: 'Meetings', id: activity },
      ],
    }),

    /**
     * @DELETE remove Meeting
     */
    deleteMeeting: builder.mutation<
      Meeting,
      { profileId?: number; courseId: number; activityId: number }
    >({
      queryFn: async ({ profileId, courseId, activityId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = profileId || currentProfile?.profileId;

          const { data } = await mainApi.delete(
            `courses/${courseId}/activities/${activityId}/mettings`,
            {
              data: {
                user_profile_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'Meetings', id: 'LIST' }],
    }),

    //
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCourseMeetingsQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
  useGetMeetingByIdQuery,
  useLazyGetMeetingByIdQuery,
  useLazyGetCourseMeetingsQuery,
} = teacherMeetingsApi;
